import React from "react"
import { Dropdown } from "antd"
import type { MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faWandMagicSparkles,
  faTerminal,
  faShuffle,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
  useQueryConsoleLayoutStore,
  QueryConsoleViewMode as ViewMode,
} from "stores/queryConsoleLayoutStore"

interface TabDef {
  mode: ViewMode
  label: string
  icon: IconDefinition
}

/** Modes shown in the dropdown. "saved" is intentionally not here — it's a
 *  toggle button next to QueryStatus in the action row, since it's a
 *  contextual action on the Console editor rather than a peer view mode. */
const TABS: TabDef[] = [
  { mode: "builder", label: "Builder", icon: faWandMagicSparkles },
  { mode: "transform", label: "Transform", icon: faShuffle },
  { mode: "console", label: "Console", icon: faTerminal },
]

/**
 * View-mode selector — compact dropdown that condenses what was previously a
 * tab ribbon. Shows the current mode (icon + label) on the trigger; clicking
 * opens a menu of the available modes.
 */
const QueryConsoleViewMode: React.FC = () => {
  const viewMode = useQueryConsoleLayoutStore((s) => s.viewMode)
  const setViewMode = useQueryConsoleLayoutStore((s) => s.setViewMode)

  // If the user is currently in "saved" view, the trigger should still
  // make sense — surface Console as the visible label since they got
  // there via the Saved toggle, not via the dropdown.
  const triggerMode: ViewMode =
    viewMode === "saved" ? "console" : viewMode
  const current = TABS.find((t) => t.mode === triggerMode) ?? TABS[0]

  const items: MenuProps["items"] = TABS.map((tab) => ({
    key: tab.mode,
    label: (
      <span className="qc-view-mode__menu-item">
        <FontAwesomeIcon icon={tab.icon} className="qc-view-mode__icon" />
        {tab.label}
      </span>
    ),
    onClick: () => setViewMode(tab.mode),
  }))

  return (
    <Dropdown
      menu={{ items, selectedKeys: [triggerMode] }}
      trigger={["click"]}
      placement="bottomLeft"
      autoAdjustOverflow={false}
      overlayClassName="qc-view-mode-overlay"
    >
      <button
        type="button"
        className="qc-view-mode__trigger"
        aria-label="Change view mode"
      >
        <FontAwesomeIcon icon={current.icon} className="qc-view-mode__icon" />
        <span className="qc-view-mode__label">{current.label}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className="qc-view-mode__caret"
        />
      </button>
    </Dropdown>
  )
}

export default QueryConsoleViewMode
